import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api, ApiRequestError } from '@/lib/api-client'

interface MeResponse {
  id: string
  email: string
  name: string
  role: string
  mfa_enabled: number
  mfa_enforced: number
  status: string
  last_login_at: string | null
  tenant_id: string
  tenant_name: string
  tenant_slug: string
  plan: string
  settings_json: string
}

// SEC: las respuestas de login/verify-mfa ya no devuelven tokens —
// la sesión vive solo en cookies HttpOnly. Solo persisten en el body:
// - user (datos del usuario)
// - tempToken (multi-step: login con MFA pendiente)
// - setupToken (multi-step: MFA setup obligatorio)
type LoginResponse =
  | { user: { id: string; email: string; name: string; role: string } }
  | { mfaRequired: true; tempToken: string }
  | { mfaSetupRequired: true; setupToken: string }

type MfaVerifyResponse = {
  user: { id: string; email: string; name: string; role: string }
}

export function useAuth() {
  const queryClient = useQueryClient()

  // No podemos inspeccionar la cookie HttpOnly desde JS, así que dejamos
  // que /auth/me decida si hay sesión válida. Si responde 401 el cliente
  // gestiona el refresh y, si tampoco eso funciona, redirige a /login.
  const {
    data: user,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['auth', 'me'],
    queryFn: () => api.get<MeResponse>('/auth/me'),
    retry: false,
    staleTime: 10 * 60 * 1000,
  })

  const loginMutation = useMutation({
    mutationFn: (data: { email: string; password: string }) =>
      api.post<LoginResponse>('/auth/login', data),
    onSuccess: (result) => {
      if (!('mfaRequired' in result) && !('mfaSetupRequired' in result)) {
        queryClient.invalidateQueries({ queryKey: ['auth'] })
      }
    },
  })

  const verifyMfaMutation = useMutation({
    mutationFn: (data: { tempToken: string; code: string }) =>
      api.post<MfaVerifyResponse>('/auth/login/verify-mfa', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['auth'] })
    },
  })

  const logoutMutation = useMutation({
    mutationFn: async () => {
      try {
        // La API borra las cookies access/refresh y la sesión en BD.
        await api.post('/auth/logout')
      } finally {
        // Limpia la caché de react-query y la impersonación de tenant
        // (no es secreto pero ya no aplica sin sesión).
        localStorage.removeItem('roi365_active_tenant')
        queryClient.clear()
      }
    },
    onSuccess: () => {
      window.location.href = '/login'
    },
  })

  const isAuthenticated = !!user && !error

  return {
    user: user ?? null,
    isLoading,
    isAuthenticated,
    login: loginMutation.mutateAsync,
    loginError: loginMutation.error as ApiRequestError | null,
    isLoggingIn: loginMutation.isPending,
    verifyMfa: verifyMfaMutation.mutateAsync,
    verifyMfaError: verifyMfaMutation.error as ApiRequestError | null,
    isVerifyingMfa: verifyMfaMutation.isPending,
    logout: logoutMutation.mutateAsync,
  }
}
